import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { User, UserDetail, SecurityUserDetail, LoadingState, AppError } from '../types';

interface FileState {
  // 当前文件信息
  currentFile: User | null;
  setCurrentFile: (file: User | null) => void;
  
  // 文件列表
  files: User[];
  setFiles: (files: User[]) => void;
  addFile: (file: User) => void;
  updateFile: (username: string, updates: Partial<User>) => void;
  removeFile: (username: string) => void;
  
  // 文件详情
  fileDetails: Record<string, UserDetail>;
  setFileDetail: (username: string, detail: UserDetail) => void;
  
  // 文件安全信息
  fileSecureInfos: Record<string, SecurityUserDetail>;
  setFileSecureInfo: (username: string, secureInfo: SecurityUserDetail) => void;
  
  // 加载状态
  loadingStates: Record<string, LoadingState>;
  setLoadingState: (key: string, state: LoadingState) => void;
  
  // 错误信息
  errors: Record<string, AppError | null>;
  setError: (key: string, error: AppError | null) => void;
  clearError: (key: string) => void;
  
  // 重置状态
  reset: () => void;
}

const initialState = {
  currentFile: null,
  files: [],
  fileDetails: {},
  fileSecureInfos: {},
  loadingStates: {},
  errors: {},
};

export const useFileStore = create<FileState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      
      setCurrentFile: (file) => {
        set({ currentFile: file });
      },
      
      setFiles: (files) => {
        set({ files });
      },
      
      addFile: (file) => {
        const { files } = get();
        set({ files: [...files, file] });
      },
      
      updateFile: (username, updates) => {
        const { files } = get();
        const updatedFiles = files.map(file => 
          file.username === username ? { ...file, ...updates } : file
        );
        set({ files: updatedFiles });
      },
      
      removeFile: (username) => {
        const { files } = get();
        const filteredFiles = files.filter(file => file.username !== username);
        set({ files: filteredFiles });
      },
      
      setFileDetail: (username, detail) => {
        const { fileDetails } = get();
        set({ 
          fileDetails: { ...fileDetails, [username]: detail } 
        });
      },
      
      setFileSecureInfo: (username, secureInfo) => {
        const { fileSecureInfos } = get();
        set({ 
          fileSecureInfos: { ...fileSecureInfos, [username]: secureInfo } 
        });
      },
      
      setLoadingState: (key, state) => {
        const { loadingStates } = get();
        set({ 
          loadingStates: { ...loadingStates, [key]: state } 
        });
      },
      
      setError: (key, error) => {
        const { errors } = get();
        set({ 
          errors: { ...errors, [key]: error } 
        });
      },
      
      clearError: (key) => {
        const { errors } = get();
        const { [key]: _, ...restErrors } = errors;
        set({ errors: restErrors });
      },
      
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'file-store',
    }
  )
);
